import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  CalendarDays, User, Mail, Phone, Calendar, 
  Users, Clipboard, Sparkles, CheckCircle2, Ticket, 
  ArrowLeft, ArrowRight, ShieldCheck, Tag 
} from 'lucide-react';
import { toursData } from '../data/tours';
import './BookingForm.css';

export default function BookingForm() {
  const [searchParams] = useSearchParams();
  
  // URL Pre-fill parameters
  const tourParam = searchParams.get('tour') || '';
  const guestsParam = Number(searchParams.get('guests')) || 1;
  const dateParam = searchParams.get('date') || '';

  // Form State
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedTourId, setSelectedTourId] = useState(tourParam);
  const [departureDate, setDepartureDate] = useState(dateParam);
  const [guestsCount, setGuestsCount] = useState(guestsParam);
  const [specialRequests, setSpecialRequests] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoDiscount, setPromoDiscount] = useState(0);

  // Progressive Step state
  const [step, setStep] = useState(1);
  
  // Success Booking Details
  const [isSuccess, setIsSuccess] = useState(false);
  const [finalBooking, setFinalBooking] = useState(null);

  // Sync selected tour from parameters
  useEffect(() => {
    if (tourParam) {
      setSelectedTourId(tourParam);
    }
  }, [tourParam]);

  // Find active tour data
  const selectedTour = toursData.find(t => t.id === selectedTourId);
  const basePrice = selectedTour ? selectedTour.price * guestsCount : 0;
  const gstAmount = basePrice * 0.05;
  const discountAmount = promoApplied ? basePrice * promoDiscount : 0;
  const totalPrice = basePrice + gstAmount - discountAmount;

  const handleApplyPromo = () => {
    if (promoCode.trim().toUpperCase() === 'WELCOME10') {
      setPromoApplied(true);
      setPromoDiscount(0.10); // 10% discount
    } else {
      alert("Invalid promo code. Try 'WELCOME10' for a 10% discount!");
    }
  };

  const handleNextStep = () => {
    if (step === 1) {
      if (!selectedTourId || !departureDate) {
        alert("Please select a tour package and departure date to proceed.");
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!fullName || !email || !phone) {
        alert("Please fill in your name, email, and phone number to continue.");
        return;
      }
      // Simple email & phone regex validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        alert("Please enter a valid email address.");
        return;
      }
      setStep(3);
    }
  };

  const handlePrevStep = () => {
    setStep(prev => Math.max(1, prev - 1));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!fullName || !email || !phone || !selectedTourId || !departureDate) {
      alert("Missing details. Please complete all form steps.");
      return;
    }

    // Generate random reference number
    const refNum = `HK-${Math.floor(100000 + Math.random() * 900000)}`;
    
    const newBooking = {
      referenceNumber: refNum,
      fullName,
      email,
      phone,
      tourId: selectedTourId,
      tourTitle: selectedTour.title,
      tourPrice: selectedTour.price,
      departureDate,
      guestsCount,
      totalPrice,
      discountAmount,
      specialRequests,
      bookingDate: new Date().toLocaleDateString()
    };

    // Save to LocalStorage
    const existingBookings = JSON.parse(localStorage.getItem('hk_bookings') || '[]');
    existingBookings.push(newBooking);
    localStorage.setItem('hk_bookings', JSON.stringify(existingBookings));

    // Show Success State
    setFinalBooking(newBooking);
    setIsSuccess(true);

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isSuccess && finalBooking) {
    return (
      <div className="booking-page container section">
        <div className="booking-success-card glass-card">
          <div className="success-icon-wrapper animate-bounce">
            <CheckCircle2 size={48} className="success-icon" />
          </div>
          <h2 className="success-title">Your Journey is Confirmed!</h2>
          <p className="success-subtitle">Thank you, {finalBooking.fullName}. A senior travel curator from Holiday Kart will connect with you via call/WhatsApp within 24 hours.</p>

          <div className="ticket-layout">
            <div className="ticket-header">
              <div className="ticket-brand">
                <Ticket size={24} className="ticket-icon" />
                <span>Holiday<span className="logo-accent">Kart</span> Boarding Pass</span>
              </div>
              <div className="ticket-ref">
                <span>REFERENCE NUMBER:</span>
                <strong>{finalBooking.referenceNumber}</strong>
              </div>
            </div>

            <div className="ticket-body">
              <div className="ticket-row">
                <div className="ticket-field">
                  <span>Guest Name</span>
                  <strong>{finalBooking.fullName}</strong>
                </div>
                <div className="ticket-field">
                  <span>Tour Package</span>
                  <strong>{finalBooking.tourTitle}</strong>
                </div>
              </div>

              <div className="ticket-row">
                <div className="ticket-field">
                  <span>Departure Date</span>
                  <strong>{new Date(finalBooking.departureDate).toLocaleDateString('en-IN', {
                    day: 'numeric', month: 'long', year: 'numeric'
                  })}</strong>
                </div>
                <div className="ticket-field">
                  <span>Total Guests</span>
                  <strong>{finalBooking.guestsCount} {finalBooking.guestsCount === 1 ? 'Traveler' : 'Travelers'}</strong>
                </div>
              </div>

              <div className="ticket-row">
                <div className="ticket-field">
                  <span>Contact Details</span>
                  <strong>{finalBooking.email} | {finalBooking.phone}</strong>
                </div>
                <div className="ticket-field">
                  <span>Grand Total</span>
                  <strong className="ticket-price">₹{finalBooking.totalPrice.toLocaleString('en-IN')}</strong>
                </div>
              </div>

              {finalBooking.specialRequests && (
                <div className="ticket-row special-row">
                  <div className="ticket-field">
                    <span>Special Notes / Preferences</span>
                    <p className="ticket-notes">"{finalBooking.specialRequests}"</p>
                  </div>
                </div>
              )}
            </div>
            <div className="ticket-footer-strip">
              <span>Have an unforgettable vacation with Holiday Kart</span>
            </div>
          </div>

          <div className="success-actions">
            <Link to="/" className="btn btn-primary">
              Return to Home
            </Link>
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setIsSuccess(false);
                setStep(1);
                setFullName('');
                setEmail('');
                setPhone('');
                setSpecialRequests('');
                setSelectedTourId('');
                setDepartureDate('');
                setGuestsCount(1);
                setPromoCode('');
                setPromoApplied(false);
                setPromoDiscount(0);
              }}
            >
              Book Another Tour
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-page container section">
      {/* Progressive Step Progress indicators */}
      <div className="booking-steps-indicator">
        <div className={`step-node ${step >= 1 ? 'node-active' : ''} ${step > 1 ? 'node-completed' : ''}`}>
          <div className="node-number">1</div>
          <span>Package & Date</span>
        </div>
        <div className="step-connector-line">
          <div className="connector-progress" style={{ width: step === 1 ? '0%' : step === 2 ? '50%' : '100%' }}></div>
        </div>
        <div className={`step-node ${step >= 2 ? 'node-active' : ''} ${step > 2 ? 'node-completed' : ''}`}>
          <div className="node-number">2</div>
          <span>Traveler Details</span>
        </div>
        <div className="step-connector-line">
          <div className="connector-progress" style={{ width: step <= 2 ? '0%' : '100%' }}></div>
        </div>
        <div className={`step-node ${step === 3 ? 'node-active' : ''}`}>
          <div className="node-number">3</div>
          <span>Confirm Booking</span>
        </div>
      </div>

      <div className="booking-layout">
        {/* Left Side - Progressive Booking Form */}
        <div className="booking-form-area glass-card">
          
          <form onSubmit={handleSubmit} className="booking-main-form">
            
            {/* Step 1: Trip configuration */}
            {step === 1 && (
              <div className="form-step-panel fade-in">
                <div className="form-header">
                  <Clipboard size={24} className="form-header-icon" />
                  <div>
                    <h3>Choose Package & Date</h3>
                    <p>Select your preferred destination package and travel date details</p>
                  </div>
                </div>

                <div className="form-group select-group">
                  <label className="form-label">Select Travel Package *</label>
                  <div className="input-with-icon">
                    <Clipboard size={18} className="input-icon" />
                    <select
                      className="form-control"
                      value={selectedTourId}
                      onChange={(e) => setSelectedTourId(e.target.value)}
                      required
                    >
                      <option value="">-- Choose a Tour --</option>
                      {toursData.map((tour) => (
                        <option key={tour.id} value={tour.id}>
                          {tour.title} ({tour.category} - ₹{tour.price.toLocaleString('en-IN')})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Departure Date *</label>
                    <div className="input-with-icon">
                      <Calendar size={18} className="input-icon" />
                      <input
                        type="date"
                        className="form-control"
                        value={departureDate}
                        onChange={(e) => setDepartureDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Number of Guests *</label>
                    <div className="input-with-icon">
                      <Users size={18} className="input-icon" />
                      <input
                        type="number"
                        className="form-control"
                        min="1"
                        max="20"
                        value={guestsCount}
                        onChange={(e) => setGuestsCount(Math.max(1, Number(e.target.value)))}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="form-navigation">
                  <span className="required-disclaimer">* Required inputs</span>
                  <button type="button" onClick={handleNextStep} className="btn btn-primary btn-next">
                    <span>Next: Traveler Details</span> <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Traveler details */}
            {step === 2 && (
              <div className="form-step-panel fade-in">
                <div className="form-header">
                  <User size={24} className="form-header-icon" />
                  <div>
                    <h3>Traveler Information</h3>
                    <p>Enter your primary contact details. We send invoice summaries and pass details here.</p>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <div className="input-with-icon">
                    <User size={18} className="input-icon" />
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Anjali Sharma"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Email Address *</label>
                    <div className="input-with-icon">
                      <Mail size={18} className="input-icon" />
                      <input
                        type="email"
                        className="form-control"
                        placeholder="e.g. anjali@gmail.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Phone Number *</label>
                    <div className="input-with-icon">
                      <Phone size={18} className="input-icon" />
                      <input
                        type="tel"
                        className="form-control"
                        placeholder="e.g. +91 98765 43210"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="form-navigation">
                  <button type="button" onClick={handlePrevStep} className="btn btn-secondary">
                    <ArrowLeft size={16} /> <span>Back</span>
                  </button>
                  <button type="button" onClick={handleNextStep} className="btn btn-primary btn-next">
                    <span>Next: Preferences</span> <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Preferences & confirmation */}
            {step === 3 && (
              <div className="form-step-panel fade-in">
                <div className="form-header">
                  <Sparkles size={24} className="form-header-icon" />
                  <div>
                    <h3>Preferences & Review</h3>
                    <p>Tell us about any specific details to optimize your custom travel package.</p>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Special Requests or Needs</label>
                  <textarea
                    className="form-control"
                    placeholder="Dietary requests, room configs (twin beds), flight preferences, private taxi requests, etc..."
                    value={specialRequests}
                    onChange={(e) => setSpecialRequests(e.target.value)}
                  />
                </div>

                {/* Promo code inputs */}
                <div className="promo-code-box">
                  <label className="form-label">Apply Promo Code</label>
                  <div className="promo-input-row">
                    <div className="input-with-icon flex-grow-1">
                      <Tag size={16} className="input-icon" />
                      <input
                        type="text"
                        className="form-control promo-control"
                        placeholder="e.g. WELCOME10"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        disabled={promoApplied}
                      />
                    </div>
                    <button 
                      type="button" 
                      onClick={handleApplyPromo} 
                      className={`btn ${promoApplied ? 'btn-secondary' : 'btn-outline'}`}
                      disabled={promoApplied}
                    >
                      {promoApplied ? 'Applied' : 'Apply'}
                    </button>
                  </div>
                  {promoApplied && <span className="promo-applied-success">Success! 10% discount applied to your base fare.</span>}
                </div>

                {/* Security trust badge info */}
                <div className="security-guarantee-card">
                  <ShieldCheck size={28} className="shield-icon" />
                  <p><strong>Secure Inquiry:</strong> No prepayment is taken. Our holiday curation team will review constraints, apply flights, and verify itinerary changes before invoicing.</p>
                </div>

                <div className="form-navigation">
                  <button type="button" onClick={handlePrevStep} className="btn btn-secondary">
                    <ArrowLeft size={16} /> <span>Back</span>
                  </button>
                  <button type="submit" className="btn btn-primary submit-final-btn">
                    <Sparkles size={18} />
                    <span>Request Vacation Booking</span>
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Right Side - Sticky Price Summary Sidebar */}
        <div className="booking-summary-sidebar">
          <div className="summary-sidebar-card glass-card">
            <h3>Fare Summary</h3>
            {selectedTour ? (
              <div className="sidebar-breakdown-details">
                <div className="breakdown-tour-info">
                  <img src={selectedTour.image} alt={selectedTour.title} className="tour-preview-img" />
                  <div>
                    <h4>{selectedTour.title}</h4>
                    <span className="tour-duration-tag">{selectedTour.duration}</span>
                  </div>
                </div>

                <div className="breakdown-price-list">
                  <div className="price-item">
                    <span>Base Fare (₹{selectedTour.price.toLocaleString('en-IN')} × {guestsCount})</span>
                    <span>₹{basePrice.toLocaleString('en-IN')}</span>
                  </div>
                  {promoApplied && (
                    <div className="price-item discount-item">
                      <span>Promo Discount (10%)</span>
                      <span>-₹{discountAmount.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  <div className="price-item">
                    <span>Convenience Charge</span>
                    <span className="free-tag">FREE</span>
                  </div>
                  <div className="price-item">
                    <span>GST (5%)</span>
                    <span>₹{gstAmount.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="price-item total-item">
                    <span>Total Estimated Amount</span>
                    <span className="total-amount-val">₹{totalPrice.toLocaleString('en-IN')}</span>
                  </div>
                </div>

                <div className="trust-indicator-strip">
                  <ShieldCheck size={14} className="trust-strip-icon" />
                  <span>Curator Reviewed Pricing Guarantee</span>
                </div>
              </div>
            ) : (
              <div className="no-tour-selected">
                <Clipboard size={32} className="no-tour-icon" />
                <p>Select a travel package in Step 1 to load real-time fares and breakdown estimates.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
